import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import { PostLinkAccess } from '../graphql/generated/graphql';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';
import useShareSecret from './useShareSecret';

const POST_FRAGMENT = gql(`
    fragment useSyncShareSecretWithUrl_post on Post {
        id
        linkAccess
        shareSecret
        publishedAt
        ...postPath_post
    }
`);

const useSyncShareSecretWithUrl = (
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isPublic =
    post?.linkAccess === PostLinkAccess.Public ||
    post?.linkAccess === PostLinkAccess.PublicEdit;

  const shareSecret = post?.shareSecret ?? undefined;
  const shouldUseShareSecret = post?.publishedAt
    ? isPublic
    : post?.linkAccess !== PostLinkAccess.Disabled;

  const shareSecretParam = useShareSecret();
  const hasShareSecretAsParam = shareSecretParam === shareSecret;

  useEffect(() => {
    if (!shareSecret || !post) return;

    if (!shouldUseShareSecret && hasShareSecretAsParam) {
      navigate(
        postPath(post, {
          anchor: location.hash,
          shareSecret: undefined,
        }),
        {
          replace: true,
        },
      );
    } else if (shouldUseShareSecret && !hasShareSecretAsParam) {
      navigate(
        postPath(post, {
          anchor: location.hash,
          shareSecret,
        }),
        {
          replace: true,
        },
      );
    }
  }, [
    shouldUseShareSecret,
    location,
    navigate,
    post,
    shareSecret,
    hasShareSecretAsParam,
  ]);
};

export default useSyncShareSecretWithUrl;
